import * as React from 'react';
import {Component, Expressions} from '@packages/systems/wfdl';
import {getRichTextOverlayCandidates} from '@packages/systems/overlays';
import type {LinkSettingsOverlayTargetCreateRequestedPayload} from '@packages/systems/designer/actions';
import {overlayTargetCreateRequested} from '@packages/systems/designer/flux-action-creators';
import {isUserComponent, usePlugins} from '@packages/utilities/platform';
import {
  selectComponents,
  selectCurrentComponent,
  selectCurrentComponentType,
  selectSelectedElementInFocusedComponent,
  selectSelectedNativeIdPath,
} from '@packages/utilities/platform-selectors';
import {getContentEditorActive} from '@packages/shared/states';
import {isEditingDynamicCMSContent} from '@packages/systems/ContentEditor';
import {isRichText} from '@packages/utilities/elementTypeUtils';
import {getChildren} from '@packages/utilities/core-elements';
import {createSelector} from 'reselect';
import {useExpressionEditorContext} from '@packages/systems/ExpressionEditor-utils';
import {
  useDesignerSelector,
  useDispatch,
  useFluxContext,
  type DesignerFlux,
  type DesignerStores,
} from '@packages/systems/designer/flux';
import {
  EElement,
  type Expression,
  type ExpressionElement,
} from '@packages/systems/wfdl-ast';
import {
  getProjectingAncestorIds,
  getOverlayTargetOptions,
  getOverlayTargetComponent,
  getContainingOverlayTarget,
  type OverlayTargetOptions,
  type ContainingOverlayTarget,
} from './LinkOverlayTargets';

/**
 * An overlay target is inserted as a sibling directly after the trigger, so the
 * trigger cannot be the component root — there is no sibling slot there.
 *
 * Creation is also disabled in an override context: the link value lives on
 * the instance's prop, so the newly created overlay would not be wired back to it.
 */
export const canCreateOverlayForSelection = (
  selectedElementId: string | undefined,
  currentComponentRootId: string | undefined,
  isEditingRichText: boolean,
  isOverride: boolean
): boolean =>
  selectedElementId != null &&
  currentComponentRootId != null &&
  selectedElementId !== currentComponentRootId &&
  !isEditingRichText &&
  !isOverride;

const selectAssetState = (state: DesignerStores) => state.AssetStore;
const selectStyleBlockState = (state: DesignerStores) => state.StyleBlockStore;
const selectIsEditingRichText = (state: DesignerStores) => {
  const active = getContentEditorActive(state.ContentEditorStore);
  return Boolean(active && isRichText(active.subtree));
};
type ContentEditorStores = Pick<DesignerStores, 'ContentEditorStore'>;
const selectIsEditingDynamicRichText = ({
  ContentEditorStore,
}: ContentEditorStores): boolean => {
  const active = getContentEditorActive(ContentEditorStore);
  return Boolean(
    active && isRichText(active.subtree) && isEditingDynamicCMSContent(active)
  );
};
const selectContentEditorSubtree = ({
  ContentEditorStore,
}: ContentEditorStores) => ContentEditorStore.subtree;
const selectContentEditorSelection = ({
  ContentEditorStore,
}: ContentEditorStores) => ContentEditorStore.selection;

/**
 * CMS rich text content is not in the component map, so when the trigger is a
 * component instance faux-selected in the Content Editor, targets come from
 * the edited rich text subtree instead of the current component. Only its
 * top-level candidates are offered, as those are the only targets the
 * rendered rich text resolves.
 */
export const selectCmsRichTextOverlayContext = createSelector(
  selectIsEditingDynamicRichText,
  selectContentEditorSubtree,
  selectContentEditorSelection,
  (isEditingDynamicRichText, subtree, selection) => {
    if (!isEditingDynamicRichText || !subtree) return null;
    if (selection?.type !== 'Node' || !selection.nodeId) return null;

    const render = EElement(subtree);
    const trigger = Expressions.getElementById(render, selection.nodeId);

    return trigger && isUserComponent(trigger.type)
      ? {
          render,
          trigger,
          // Discovery and rendering share the same top-level overlay scope.
          candidates: getRichTextOverlayCandidates(getChildren(subtree) ?? []),
        }
      : null;
  }
);

export const selectOverlayContext = createSelector(
  selectIsEditingRichText,
  selectCurrentComponent,
  selectSelectedElementInFocusedComponent,
  selectSelectedNativeIdPath,
  selectCmsRichTextOverlayContext,
  (
    isEditingRichText,
    currentComponent,
    trigger,
    anchorNativeIdPath,
    cmsRichTextContext
  ) => {
    const render = Component.getRender(currentComponent);

    return {
      // In CMS rich text scope, page-level overlays are intentionally not
      // offered: the saved `'cms-rich-text'` key only resolves targets within
      // the same rich text value, so a page overlay would never be wired up.
      render: cmsRichTextContext?.render ?? render,
      trigger: cmsRichTextContext?.trigger ?? trigger,
      triggerId: cmsRichTextContext?.trigger.id ?? trigger?.id,
      anchorNativeIdPath,
      currentComponentRootId: Expressions.getElement(render)?.id,
      isEditingRichText,
      isCmsRichTextScope: cmsRichTextContext !== null,
      targetCandidates: cmsRichTextContext?.candidates,
    };
  }
);

export const useOverlayTargetCreation = (
  kind: LinkSettingsOverlayTargetCreateRequestedPayload['kind'],
  componentKey: string,
  context: ReturnType<typeof selectOverlayContext>,
  isOverride: boolean
): (() => void) | undefined => {
  const {
    triggerId,
    anchorNativeIdPath,
    currentComponentRootId,
    isEditingRichText,
  } = context;
  const isEditingDefault = useExpressionEditorContext() === 'field-editor';
  const designerDispatch = useDispatch();
  const canCreate =
    !isEditingDefault &&
    canCreateOverlayForSelection(
      triggerId,
      currentComponentRootId,
      isEditingRichText,
      isOverride
    );
  const onCreate = React.useCallback(() => {
    if (!triggerId || !canCreate) return;
    designerDispatch(
      overlayTargetCreateRequested({
        kind,
        triggerId,
        anchorNativeIdPath,
        componentKey,
      })
    );
  }, [
    anchorNativeIdPath,
    canCreate,
    componentKey,
    designerDispatch,
    kind,
    triggerId,
  ]);
  return canCreate ? onCreate : undefined;
};

export const useOverlayTargetOptions = ({
  isTarget,
  missingLabel,
  selectedId,
  render,
  selectedElement,
  isEditingDefault,
  candidates,
}: {
  isTarget: (element: ExpressionElement) => boolean;
  missingLabel: string;
  selectedId: string | undefined;
  render: Expression;
  selectedElement: ExpressionElement | null;
  isEditingDefault: boolean;
  candidates: readonly ExpressionElement[] | undefined;
}): OverlayTargetOptions => {
  const components = useDesignerSelector(selectComponents);
  const plugins = usePlugins();
  const styleBlockState = useDesignerSelector(selectStyleBlockState);
  const assetState = useDesignerSelector(selectAssetState);

  return React.useMemo(() => {
    return getOverlayTargetOptions({
      render,
      components,
      plugins,
      styleBlockState,
      assetState,
      selectedId,
      isEditingDefault,
      triggerProjectingAncestorIds: selectedElement
        ? getProjectingAncestorIds(selectedElement.id, render)
        : undefined,
      candidates,
      isTarget,
      missingLabel,
    });
  }, [
    assetState,
    candidates,
    components,
    isEditingDefault,
    isTarget,
    missingLabel,
    plugins,
    render,
    selectedElement,
    selectedId,
    styleBlockState,
  ]);
};

export const useOverlayTargetComponent = (): string => {
  const currentComponentType = useDesignerSelector(selectCurrentComponentType);
  return getOverlayTargetComponent(currentComponentType);
};

export const useGetContainingOverlayTarget = (
  isTarget: (element: ExpressionElement) => boolean
): (() => ContainingOverlayTarget | null) => {
  const flux = useFluxContext<DesignerFlux>();
  const isEditingDefault = useExpressionEditorContext() === 'field-editor';

  return React.useCallback(() => {
    if (isEditingDefault) return null;

    const {render, triggerId, isCmsRichTextScope, targetCandidates} =
      selectOverlayContext(flux.state);
    if (!triggerId) return null;

    return getContainingOverlayTarget({
      render,
      components: selectComponents(flux.state),
      triggerId,
      currentComponentType: selectCurrentComponentType(flux.state),
      isCmsRichTextScope,
      candidates: targetCandidates,
      isTarget,
    });
  }, [flux, isEditingDefault, isTarget]);
};
